/**
 * Dev-only input latency probe (`?debug=latency`). Measures, per key press, the time from the
 * keydown handler to:
 *   - button: the first rendered frame whose action button shows feedback for the press
 *   - cast:   the first rendered frame showing the cast (cast bar / cast animation / cue)
 *   - accept: the sim step that applied the command
 *   - move:   the first rendered frame where the local player has visibly moved (WASD)
 *   - target: the first rendered frame whose selection ring / target frame shows a Tab pick
 * plus the input dispatch delay (keydown timeStamp → handler) and frame-time stats. Results go to a small overlay and `window.__latency`.
 */
export type ProbeMetric = 'button' | 'cast' | 'accept' | 'move' | 'target';
export type PressKind = 'ability' | 'move' | 'tab';
const METRICS: readonly ProbeMetric[] = ['button', 'cast', 'accept', 'move', 'target'];
type Series = ProbeMetric | 'dispatch' | 'frame' | 'hitch';
const SERIES: readonly Series[] = [...METRICS, 'dispatch', 'frame', 'hitch'];
/** `hitch` = the longest frame within this long after an ability press (sticky-feeling presses). */
const HITCH_WINDOW_MS = 300;

interface OpenPress {
  t0: number;
  kind: PressKind;
  key: string;
  done: Partial<Record<ProbeMetric, number>>;
}

/** Presses that never produce a metric (rejected casts) are dropped after this long. */
const OPEN_TIMEOUT_MS = 1500;
const MAX_SAMPLES = 2000;

const SPIKE_MS = 40;
const roundAll = (r: Record<string, number>): Record<string, number> => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, Math.round(v * 10) / 10]));

export function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return NaN;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[i]!;
}

export interface ProbeStats {
  n: number;
  p50: number;
  p95: number;
  max: number;
}

export function stats(values: readonly number[]): ProbeStats {
  const s = [...values].sort((a, b) => a - b);
  return { n: s.length, p50: percentile(s, 50), p95: percentile(s, 95), max: s.length ? s[s.length - 1]! : NaN };
}

export class LatencyProbe {
  readonly samples: Record<Series, number[]> = { button: [], cast: [], accept: [], move: [], target: [], dispatch: [], frame: [], hitch: [] };
  private hitchWatch: { until: number; max: number }[] = [];
  /** Work timed since the last frame (ms by label), and frames slower than SPIKE_MS with that breakdown. */
  private work: Record<string, number> = {};
  readonly spikes: { at: number; dt: number; work: Record<string, number> }[] = [];

  /** Time a piece of work (sim ticks, render, HUD, handlers) for the spike breakdown. */
  time<T>(label: string, f: () => T): T {
    const t = performance.now();
    try {
      return f();
    } finally {
      this.work[label] = (this.work[label] ?? 0) + performance.now() - t;
    }
  }
  private open: OpenPress[] = [];
  private overlay: HTMLElement | null = null;
  private lastOverlayAt = 0;

  constructor(host?: HTMLElement) {
    if (host && typeof document !== 'undefined') {
      const o = document.createElement('div');
      o.style.cssText = 'position:fixed;top:6px;left:50%;transform:translateX(-50%);z-index:9999;font:11px/1.35 ui-monospace,monospace;color:#cfe;background:rgba(0,0,0,0.72);padding:4px 8px;border-radius:4px;pointer-events:none;white-space:pre';
      host.appendChild(o);
      this.overlay = o;
    }
  }

  /**
   * A gameplay key went down. `eventTs` = KeyboardEvent.timeStamp, `now` = handler entry. Metrics
   * are timed from handler entry (what the client controls); the browser's queueing delay before
   * the handler runs (busy main thread) is reported separately as `dispatch`.
   */
  press(kind: PressKind, key: string, eventTs: number, now = eventTs): void {
    this.push('dispatch', Math.max(0, now - eventTs));
    // A new press of the same key supersedes an unfinished one.
    this.open = this.open.filter((p) => p.key !== key);
    this.open.push({ t0: now, kind, key, done: {} });
    if (kind === 'ability') this.hitchWatch.push({ until: now + HITCH_WINDOW_MS, max: 0 });
  }

  /** Record `metric` for every open press that passes `match`, at time `now`. */
  mark(metric: ProbeMetric, now: number, match: (kind: PressKind, key: string) => boolean): void {
    for (const p of this.open) {
      if (p.done[metric] !== undefined || !match(p.kind, p.key)) continue;
      p.done[metric] = now - p.t0;
      this.push(metric, now - p.t0);
    }
  }

  /** Keys still waiting for `metric`. */
  pending(metric: ProbeMetric): { kind: PressKind; key: string }[] {
    return this.open.filter((p) => p.done[metric] === undefined);
  }

  frame(dtMs: number, now: number): void {
    this.push('frame', dtMs);
    if (dtMs > SPIKE_MS && this.spikes.length < 100) this.spikes.push({ at: now, dt: dtMs, work: roundAll(this.work) });
    this.work = {};
    this.hitchWatch = this.hitchWatch.filter((h) => {
      h.max = Math.max(h.max, dtMs);
      if (now < h.until) return true;
      this.push('hitch', h.max);
      return false;
    });
    this.open = this.open.filter((p) => {
      const need = p.kind === 'move' ? (['move', 'accept'] as const) : p.kind === 'tab' ? (['target'] as const) : (['button', 'cast', 'accept'] as const);
      return now - p.t0 < OPEN_TIMEOUT_MS && need.some((m) => p.done[m] === undefined);
    });
    if (this.overlay && now - this.lastOverlayAt > 250) {
      this.lastOverlayAt = now;
      this.overlay.textContent = this.report();
    }
  }

  summary(): Record<Series, ProbeStats> {
    const out = {} as Record<Series, ProbeStats>;
    for (const m of SERIES) out[m] = stats(this.samples[m]);
    return out;
  }

  reset(): void {
    for (const k of Object.keys(this.samples) as (keyof typeof this.samples)[]) this.samples[k] = [];
    this.open = [];
    this.hitchWatch = [];
    this.spikes.length = 0;
    this.work = {};
  }

  report(): string {
    const s = this.summary();
    const f = (v: number): string => (Number.isFinite(v) ? v.toFixed(1).padStart(5) : '    -');
    const lines = ['latency ms   p50   p95   max    n'];
    for (const m of SERIES) lines.push(`${m.padEnd(10)} ${f(s[m].p50)} ${f(s[m].p95)} ${f(s[m].max)} ${String(s[m].n).padStart(4)}`);
    return lines.join('\n');
  }

  dispose(): void {
    this.overlay?.remove();
    this.overlay = null;
  }

  private push(metric: Series, v: number): void {
    const arr = this.samples[metric];
    arr.push(v);
    if (arr.length > MAX_SAMPLES) arr.shift();
  }
}

/** True when the page was opened with `?debug=latency` (or a list containing it). */
export function latencyProbeRequested(search: string): boolean {
  const v = new URLSearchParams(search).get('debug');
  return !!v && v.split(',').includes('latency');
}
